import { schedulerTypes } from './actions';
import dayjs from 'dayjs';
import { workerIdInList } from '../containers/utils/closureHelpers';

const initialState = {
  calendarLoading: false,
  calendarLoaded: false,
  /** Giorno (YYYY-MM-DD) dell'ultima coda richiesta: serve al refresh dopo una rimozione. */
  queueDate: null,
  /** Riga di coda rimossa in modo ottimistico, da ripristinare se la API fallisce. */
  queueRollback: null,
  calendar: {
    workers: [],
    appointments: {},
    services: [],
    queue: [],
    availableSlots: [],
    detaglioPrenotazioneRicorrente: {},
  },
};

export default (state = initialState, action) => {
  switch (action.type) {
    case schedulerTypes.GET_CALENDAR_DATA.REQUEST:
      return {
        ...state,
        calendarLoading: action.options?.skipLoader ? state.calendarLoading : true,
      };
    case schedulerTypes.GET_CALENDAR_DATA.SUCCESS:
      return {
        ...state,
        calendarLoading: false,
        calendarLoaded: true,
        calendar: {
          workers: [...(action.payload.mypoltrone || [])].sort(
            (a, b) => a.ordinamento - b.ordinamento,
          ),
          appointments: action.payload.prenotazioni,
          services: action.payload.servizi,
          queue: state.calendar.queue,
        },
      };
    case schedulerTypes.GET_CALENDAR_DATA.FAILED:
      return {
        ...state,
        calendarLoading: false,
        calendarLoaded: true,
      };
    case schedulerTypes.GET_DETAILS_PRENOTAZIONE_RICORRENTE.SUCCESS:
      return {
        ...state,
        detaglioPrenotazioneRicorrente: action.payload || {},
      };
    case schedulerTypes.GET_CALENDAR_QUEUE.REQUEST:
      return action.payload?.date ? { ...state, queueDate: action.payload.date } : state;
    case schedulerTypes.GET_CALENDAR_QUEUE.SUCCESS:
      return {
        ...state,
        calendar: {
          ...state.calendar,
          queue: action.payload,
        },
      };

    // Rimozione ottimistica: la riga sparisce subito, torna solo se la API fallisce.
    case schedulerTypes.REMOVE_FROM_QUEUE.REQUEST: {
      const targetId = Number(action.payload?.idListaAttesa);
      if (!Number.isFinite(targetId)) return state;
      let rollback = null;
      const nextQueue = (state.calendar.queue || []).map((worker) => {
        const entries = worker?.listaAttesa || [];
        const index = entries.findIndex((entry) => Number(entry?.idListaAttesa) === targetId);
        if (index === -1) return worker;
        rollback = { idpoltrona: worker.idpoltrona, index, entry: entries[index] };
        return {
          ...worker,
          listaAttesa: [...entries.slice(0, index), ...entries.slice(index + 1)],
        };
      });
      if (!rollback) return state;
      return {
        ...state,
        queueRollback: rollback,
        calendar: { ...state.calendar, queue: nextQueue },
      };
    }
    case schedulerTypes.REMOVE_FROM_QUEUE.SUCCESS:
      return state.queueRollback ? { ...state, queueRollback: null } : state;
    case schedulerTypes.REMOVE_FROM_QUEUE.FAILED: {
      const rollback = state.queueRollback;
      if (!rollback) return state;
      const restoredQueue = (state.calendar.queue || []).map((worker) => {
        if (worker?.idpoltrona !== rollback.idpoltrona) return worker;
        const entries = [...(worker.listaAttesa || [])];
        entries.splice(Math.min(rollback.index, entries.length), 0, rollback.entry);
        return { ...worker, listaAttesa: entries };
      });
      return {
        ...state,
        queueRollback: null,
        calendar: { ...state.calendar, queue: restoredQueue },
      };
    }
    case schedulerTypes.GET_CALENDAR_SLOTS.SUCCESS:
      return {
        ...state,
        availableSlots: action.payload || [],
      };
    case schedulerTypes.GET_CALENDAR_SLOTS.CLEAR:
      return {
        ...state,
        availableSlots: [],
      };
    case schedulerTypes.EDIT_CALENDAR_TASK.SUCCESS: {
      const taskDay = dayjs(action.payload.data_prenotazione).format('YYYY-MM-DD');
      const nextAppointments = { ...state.calendar.appointments };
      if (!nextAppointments[taskDay]) {
        nextAppointments[taskDay] = [];
      }
      return {
        ...state,
        calendar: {
          workers: state.calendar.workers,
          appointments: Object.fromEntries(
            Object.entries(nextAppointments).map(([key, bookings]) => {
              const filteredDayTasks = bookings.filter(
                (item) => item.idprenotazione !== action.payload.idprenotazione,
              );
              if (key === taskDay) {
                const previous = (bookings || []).find(
                  (item) => item.idprenotazione === action.payload.idprenotazione,
                );
                const mergedTask = {
                  ...previous,
                  ...action.payload,
                  idpoltrona: action.payload.poltrona ?? action.payload.idpoltrona ?? previous?.idpoltrona,
                  // Preserva servizi se la response di update è incompleta
                  servizi:
                    action.payload.servizi?.length > 0
                      ? action.payload.servizi
                      : previous?.servizi || [],
                };
                return [key, [...filteredDayTasks, mergedTask]];
              }
              return [key, filteredDayTasks];
            }),
          ),
          services: state.calendar.services,
          queue: state.calendar.queue,
        },
      };
    }

    case schedulerTypes.ADD_CALENDAR_TASK.SUCCESS: {
      const nextAppointments = { ...state.calendar.appointments };
      action.payload.forEach((task) => {
        const taskDay = dayjs(task.data_prenotazione).format('YYYY-MM-DD');
        task.idpoltrona = task.poltrona;

        // Controlla se esiste già una prenotazione con lo stesso idprenotazione
        const existingTaskIndex = nextAppointments[taskDay]?.findIndex(
          (t) => t.idprenotazione === task.idprenotazione
        );

        // Arricchisci i servizi con i colori dai servizi nello state (solo se manca il colore)
        if (task.servizi && Array.isArray(task.servizi) && task.servizi.length > 0) {
          task.servizi = task.servizi.map((service) => {
            // Se il servizio ha già il colore, preservalo
            if (service.color) {
              return service;
            }
            // Altrimenti cerca il colore nello state
            const serviceId = service.idServizio || service.idservezio;
            const serviceFromState = state.calendar.services.find(
              (s) => s.idServizio === serviceId || s.idservezio === serviceId
            );
            return {
              ...service,
              color: serviceFromState?.color || 'gray',
            };
          });
        }

        const dayTasks = nextAppointments[taskDay] ? [...nextAppointments[taskDay]] : [];

        if (existingTaskIndex !== undefined && existingTaskIndex >= 0) {
          // Aggiorna la prenotazione esistente (evita duplicati quando viene chiamato sia dalla saga che dall'SSE)
          const existingTask = dayTasks[existingTaskIndex];
          // Preserva i servizi esistenti se il nuovo task non li ha o li ha vuoti
          const mergedTask = {
            ...task,
            servizi: (task.servizi && task.servizi.length > 0)
              ? task.servizi
              : (existingTask.servizi || []),
          };
          dayTasks[existingTaskIndex] = mergedTask;
        } else {
          // Aggiungi la nuova prenotazione
          dayTasks.push(task);
        }
        nextAppointments[taskDay] = dayTasks;
      });
      return {
        ...state,
        calendar: {
          workers: state.calendar.workers,
          appointments: nextAppointments,
          services: state.calendar.services,
          queue: state.calendar.queue,
        },
      };
    }

    case schedulerTypes.ADD_CALENDAR_BREAK.SUCCESS: {
      const nextAppointments = { ...state.calendar.appointments };
      let { payload } = action;
      payload = {
        ...payload,
        idprenotazione: Math.floor(Math.random() * 1000000) + 1,
        self_prenotatione: 1,
        data_prenotazione: dayjs(payload.dataPrenotazione).toDate().toISOString(),
        stato: '0',
        orario_start: payload.oraInizio,
        orario_end: payload.oraFine,
        isBreak: 1,
        nota: 'PAUSA',
        ordinamento: 0,
        createAt: new Date().toISOString(),
        servizi: [],
        status: 0,
        tipo: 1,
        visible: 1,
        disabled: 0,
        iconPoltrona: 1,
        isFromGoogle: 0,
        isFromPrivateApp: 0,
        onlyApp: 0,
        disabilitato: 0,
        nome: 'PAUSA',
      };
      payload.poltrone.forEach((worker) => {
        const taskDay = dayjs(action.payload.data_prenotazione).format('YYYY-MM-DD');
        payload['poltrona'] = worker.idpoltrona;
        payload['idpoltrona'] = worker.idpoltrona;

        nextAppointments[taskDay] = nextAppointments[taskDay]
          ? [...nextAppointments[taskDay], payload]
          : [payload];
      });
      return {
        ...state,
        calendar: {
          ...state.calendar,
          appointments: nextAppointments,
        },
      };
    }

    case schedulerTypes.SET_CALENDAR_TASK.SUCCESS: {
      action.payload.forEach((task) => {
        const taskDay = dayjs(task.data_prenotazione).format('YYYY-MM-DD');
        if (!state.calendar.appointments[taskDay]) {
          state.calendar.appointments[taskDay] = [];
        }
      });

      return {
        ...state,
        calendar: {
          workers: state.calendar.workers,
          appointments: Object.fromEntries(
            Object.entries(state.calendar.appointments).map((item) => {
              const filteredTasks = item[1].filter(
                (item) =>
                  !action.payload.some((task) => item.idprenotazione === task.idprenotazione),
              );
              const tasksToAdd = action.payload.filter(
                (task) =>
                  item[0] === dayjs(task.data_prenotazione).format('YYYY-MM-DD') && !task.disabled,
              );
              return [item[0], [...filteredTasks, ...tasksToAdd]];
            }),
          ),
          services: state.calendar.services,
          queue: state.calendar.queue,
        },
      };
    }

    case schedulerTypes.DELETE_CALENDAR_TASK.SUCCESS: {
      const { all: deleteAll, id: idPreno, ricorrente: idRecurrence } = action.payload;
      const newAppointments = Object.fromEntries(
        Object.entries(state.calendar.appointments).map((item) => {
          const filteredDayTasks = item[1].filter(
            (item) => deleteAll ? item.ricorrente !== idRecurrence : item.idprenotazione !== idPreno,
          );
          return [item[0], filteredDayTasks];
        }),
      );
      return {
        ...state,
        calendar: {
          workers: state.calendar.workers,
          appointments: newAppointments,
          services: state.calendar.services,
          queue: state.calendar.queue,
        },
      };
    }

    case schedulerTypes.SET_CALENDAR_WORKER_EXTRA_WORKING_TIME.SUCCESS: {
      const {
        activeWorkersIds,
        dataInizio,
        oraInizioMattina,
        oraFineMattina,
        oraInizioPomeriggio,
        oraFinePomeriggio,
      } = action.payload;
      return {
        ...state,
        calendar: {
          workers: state.calendar.workers.map((worker) => {
            if (!workerIdInList(activeWorkersIds, worker.idpoltrona)) return worker;

            const isClose =
              oraInizioMattina === '00:00:00' &&
              oraFineMattina === '00:00:00' &&
              oraInizioPomeriggio === '00:00:00' &&
              oraFinePomeriggio === '00:00:00';
            if (isClose) {
              return {
                ...worker,
                chiusure: {
                  ...(worker.chiusure || {}),
                  [dataInizio]: [{ dalle: '00:00:00', alle: '00:00:00' }],
                },
              };
            }

            const isFullDay = oraFineMattina === '00:00:00' && oraInizioPomeriggio === '00:00:00';
            if (isFullDay) {
              return {
                ...worker,
                chiusure: {
                  ...(worker.chiusure || {}),
                  [dataInizio]: [
                    { dalle: '00:00:00', alle: oraInizioMattina },
                    { dalle: oraFinePomeriggio, alle: '00:00:00' },
                  ],
                },
              };
            }
            return {
              ...worker,
              chiusure: {
                ...(worker.chiusure || {}),
                [dataInizio]: [
                  { dalle: '00:00:00', alle: oraInizioMattina },
                  { dalle: oraFineMattina, alle: oraInizioPomeriggio },
                  { dalle: oraFinePomeriggio, alle: '00:00:00' },
                ],
              },
            };
          }),
          appointments: state.calendar.appointments,
          services: state.calendar.services,
          queue: state.calendar.queue,
        },
      };
    }

    case schedulerTypes.SET_CALENDAR_WORKER_CLOSURES.SUCCESS: {
      const { activeWorkersIds, dataChiusura, dataChiusuraFine } = action.payload;
      const start = dayjs(dataChiusura);
      const end = dayjs(dataChiusuraFine || dataChiusura);
      const dates = [];
      if (start.isValid() && end.isValid()) {
        let cursor = start.startOf('day');
        const last = end.startOf('day');
        while (cursor.isBefore(last) || cursor.isSame(last, 'day')) {
          dates.push(cursor.format('YYYY-MM-DD'));
          cursor = cursor.add(1, 'day');
        }
      } else if (dataChiusura) {
        dates.push(dataChiusura);
      }

      return {
        ...state,
        calendar: {
          ...state.calendar,
          workers: state.calendar.workers.map((worker) => {
            if (!workerIdInList(activeWorkersIds, worker.idpoltrona)) return worker;
            const nextChiusure = { ...(worker.chiusure || {}) };
            dates.forEach((date) => {
              nextChiusure[date] = [{ dalle: '00:00:00', alle: '00:00:00' }];
            });
            return {
              ...worker,
              chiusure: nextChiusure,
            };
          }),
        },
      };
    }

    case schedulerTypes.REMOVE_CALENDAR_WORKER_CLOSURES.SUCCESS: {
      const { activeWorkersIds, dataChiusura } = action.payload;
      const dateKey = dataChiusura ? dayjs(dataChiusura).format('YYYY-MM-DD') : null;
      if (!dateKey || dateKey === 'Invalid Date') return state;

      return {
        ...state,
        calendar: {
          ...state.calendar,
          workers: state.calendar.workers.map((worker) => {
            if (!workerIdInList(activeWorkersIds, worker.idpoltrona)) return worker;
            if (!worker.chiusure || typeof worker.chiusure !== 'object') return worker;
            const nextChiusure = { ...worker.chiusure };
            delete nextChiusure[dateKey];
            delete nextChiusure[dayjs(dateKey).format('DD/MM/YYYY')];
            delete nextChiusure[dayjs(dateKey).format('DD/MM/YY')];
            return {
              ...worker,
              chiusure: nextChiusure,
            };
          }),
        },
      };
    }

    default:
      return state;
  }
};
